import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import { DatePicker, createDateLabel } from "@hortis/ui/date-picker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@hortis/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Calendar } from "@hortis/ui/icons";
import { toast } from "@hortis/ui/sonner";
import { A, F, pipe } from "@mobily/ts-belt";
import { useQueryClient } from "@tanstack/react-query";
import { PlantMaterialStatus, namedOperations } from "generated/graphql";
import { type ReactNode, type RefObject, useEffect } from "react";
import { useForm } from "react-hook-form";
import { updatePlantMaterialsDocument } from "src/features/actions-menu/graphql";
import { createDateAsUTC } from "src/utils/convert-dates-to-iso";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { z } from "zod";
import { type MaterialSchema } from "../add-plant-material-form/schema";

const FormSchema = z.object({
  dateRemoved: z.date({
    required_error: "Date removed is required",
  }),
});

export const MakeAbsentDialog = ({
  triggerRef,
  open,
  onOpenChange,
  onSuccess,
  selectedMaterials,
  onSubmitOverride,
  children,
}: {
  triggerRef?: RefObject<HTMLButtonElement | null>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess: () => Promise<void> | void;
  onSubmitOverride?: (material: Partial<MaterialSchema>) => void;
  children?: ReactNode;
  selectedMaterials: Array<{ id: string; status: PlantMaterialStatus }>;
}) => {
  const queryClient = useQueryClient();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: { dateRemoved: new Date() },
  });

  const updatePlantMaterials = useGqlMutation({
    mutation: updatePlantMaterialsDocument,
    onError: () => {
      toast.error("Failed to update materials");
    },
    onSuccess: (res) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
      if (!res.updatePlantMaterials?.success) {
        toast.error("Failed to update materials");
        return;
      }
      toast.success(
        `Successfully updated ${
          res.updatePlantMaterials.plantMaterials.length
        } material${
          res.updatePlantMaterials.plantMaterials.length > 1 ? "s" : ""
        }`,
      );
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      void onSuccess();
    },
  });

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const ids = pipe(
      selectedMaterials,
      A.filter((m) => m.status !== status),
      A.map(({ id }) => id),
      F.toMutable,
    );
    if (ids.length === 0) {
      void onSuccess();
      return;
    }
    if (onSubmitOverride == null) {
      await updatePlantMaterials.mutateAsync({
        ids,
        input: {
          status: PlantMaterialStatus.Absent,
          firstAbsent: createDateAsUTC(data.dateRemoved),
        },
      });
    } else {
      onSubmitOverride({
        status: PlantMaterialStatus.Absent,
        firstAbsent: data.dateRemoved,
      });
    }
  };

  useEffect(() => {
    if (open === true) {
      form.reset({ dateRemoved: new Date() });
    }
  }, [form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <DialogContent data-cy="make-absent-dialog" triggerRef={triggerRef}>
        <Form {...form}>
          <form
            className="space-y-8"
            onSubmit={(e) => {
              e.stopPropagation();
              e.preventDefault();
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            <div className="space-y-6">
              <DialogHeader>
                <DialogTitle>Make material absent</DialogTitle>
                <DialogDescription>
                  Please enter the date the material was removed.
                </DialogDescription>
              </DialogHeader>
              <FormField
                control={form.control}
                name="dateRemoved"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date removed</FormLabel>
                    <DatePicker data-cy="date-removed-picker" {...field}>
                      <Button endIcon={Calendar} className="justify-between">
                        {createDateLabel(field.value)}
                      </Button>
                    </DatePicker>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="tertiaryGray">Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                type="submit"
                loading={form.formState.isSubmitting}
                testId="make-absent-confirm"
              >
                Confirm
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
